import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Radio,
  Settings as SettingsIcon,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DashboardPanel } from "@/components/dashboard/DashboardPanel";
import { cn } from "@/lib/utils";

interface SystemStatus {
  service: string;
  status: "connected" | "error" | "warning";
  message: string;
  detail: string;
}

const getStatusText = (value: unknown, fallback: string) => {
  if (typeof value === "string" && value.trim()) return value;
  if (value instanceof Error) return value.message;
  if (value && typeof value === "object") {
    const message = (value as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;

    const error = (value as { error?: unknown }).error;
    if (error) return getStatusText(error, fallback);
  }

  return fallback;
};

export const SystemHealthWidget = () => {
  const navigate = useNavigate();
  const [systems, setSystems] = useState<SystemStatus[]>([
    { service: "Gmail", status: "warning", message: "Checking", detail: "Email sync" },
    { service: "Calls", status: "warning", message: "Checking", detail: "Call monitor" },
  ]);

  useEffect(() => {
    const checkGmail = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("gmail-oauth", { body: { action: "status" } });
        const ok = !error && data?.connected === true;

        setSystems((prev) =>
          prev.map((item) =>
            item.service === "Gmail"
              ? {
                  ...item,
                  status: ok ? "connected" : "error",
                  message: ok ? "Connected" : "Offline",
                  detail: ok ? "Mailbox healthy" : "Reconnect integration",
                }
              : item,
          ),
        );
      } catch {
        setSystems((prev) =>
          prev.map((item) =>
            item.service === "Gmail"
              ? { ...item, status: "error", message: "Offline", detail: "Reconnect integration" }
              : item,
          ),
        );
      }
    };

    const checkCalls = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("call-monitor-session");
        const ok = !error && Boolean(data?.client_secret?.value || data?.client_secret);
        const failureDetail = getStatusText(error ?? data?.error, "Needs attention");

        setSystems((prev) =>
          prev.map((item) =>
            item.service === "Calls"
              ? {
                  ...item,
                  status: ok ? "connected" : "error",
                  message: ok ? "Live" : "Offline",
                  detail: ok ? "Voice monitor ready" : failureDetail,
                }
              : item,
          ),
        );
      } catch {
        setSystems((prev) =>
          prev.map((item) =>
            item.service === "Calls"
              ? { ...item, status: "error", message: "Offline", detail: "Needs attention" }
              : item,
          ),
        );
      }
    };

    checkGmail();
    checkCalls();

    const interval = setInterval(checkGmail, 60000);
    return () => clearInterval(interval);
  }, []);

  const summary = useMemo(() => {
    const connectedCount = systems.filter((item) => item.status === "connected").length;
    const total = systems.length;
    const hasErrors = systems.some((item) => item.status === "error");

    return {
      label: hasErrors ? "Needs attention" : connectedCount === total ? "All systems operational" : "Checking systems",
      tone: hasErrors ? "text-warning" : "text-success",
      badge: `${connectedCount}/${total} connected`,
    };
  }, [systems]);

  const renderIcon = (status: SystemStatus["status"]) => {
    if (status === "connected") return <CheckCircle2 className="h-4 w-4 text-success" />;
    if (status === "error") return <XCircle className="h-4 w-4 text-destructive" />;
    return <AlertCircle className="h-4 w-4 text-warning" />;
  };

  return (
    <DashboardPanel
      eyebrow="Platform status"
      title="System Health"
      description="Live integration readiness across your workspace"
      icon={<Activity className="h-5 w-5" />}
      className="h-full"
      contentClassName="space-y-4 p-5"
      action={
        <Button
          size="icon"
          variant="ghost"
          className="h-9 w-9 rounded-xl border border-border/60 bg-background/70"
          onClick={() => navigate("/settings?tab=integrations")}
          aria-label="Manage integrations"
        >
          <SettingsIcon className="h-4 w-4" />
        </Button>
      }
    >
      <div className="rounded-xl border border-border/70 bg-background/70 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={cn("text-sm font-semibold", summary.tone)}>{summary.label}</p>
            <p className="mt-1 text-xs text-muted-foreground">Monitor integrations before your team starts the day.</p>
          </div>
          <Badge variant="outline" className="border-border/70 bg-card text-xs font-medium">
            <Radio className="mr-1 h-3 w-3" />
            {summary.badge}
          </Badge>
        </div>
      </div>

      <div className="grid gap-3">
        {systems.map((item) => (
          <button
            key={item.service}
            onClick={() => navigate("/settings?tab=integrations")}
            className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-background/70 px-4 py-3 text-left transition-all hover:border-primary/40 hover:bg-accent/50"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border",
                  item.status === "connected" && "border-success/20 bg-success/10 text-success",
                  item.status === "error" && "border-destructive/20 bg-destructive/10 text-destructive",
                  item.status === "warning" && "border-warning/20 bg-warning/10 text-warning",
                )}
              >
                {renderIcon(item.status)}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">{item.service}</p>
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      item.status === "connected" && "bg-success",
                      item.status === "error" && "bg-destructive",
                      item.status === "warning" && "bg-warning",
                    )}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{item.detail}</p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-sm font-semibold text-foreground">{item.message}</p>
              <p className="text-xs text-muted-foreground">Open settings</p>
            </div>
          </button>
        ))}
      </div>
    </DashboardPanel>
  );
};